import React, { useCallback, useEffect, useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import { useLazyQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Atoms
import { DashboardWebLink, Loading } from "@atoms/index";
// endregion Atoms
// region Molecules
import { BaseCardItem } from "@molecules/index";
// endregion Molecules
// region Utils
import utils from "@helpers/utils";
// endregion Utils
// region Hooks
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "@hooks/useAuth";
// endregion Hooks
// region Store
import { ModulesID } from "@store/constants";
// endregion Store
// region Assets
import { concreting } from "@assets/applications/index";
// endregion Assets
// region Graphql
import { travelAllWithPagination, TTravelAllWithPaginationQueries } from "@graphql/groups/travel/query.graphql";
import {
  ECommandResultStatus,
  ETravelStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  TravelQueriesGetAllWithPaginationArgs,
  TravelType
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "src/store/graphql/groups/permissionUser/query.graphql";
// endregion Graphql
// region Languages
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Languages
// region Styles
import * as Styled from "./styles";
// endregion Styles

const DashboardWebTravels: React.FC = () => {

  const { t } = useTranslation();
  const { getDecodedToken } = useAuth();
  const { addToast } = useToast();
  const { value: selectedClient } = useSelectClient();

  const token = getDecodedToken();

  const [whereConcretingIds, setWhereConcretingIds] = useState<string>("");
  const [travels, setTravels] = useState<TravelType[]>([]);
  const [travelModalIsOpen, setTravelModalIsOpen] = useState<boolean>(false);

  // region Get Permissions according to the user and the client
  const [
    getPermissions,
    {
      loading: loadingPermissions,
      error: errorPermissions,
      data: dataPermissions
    }
  ] = useLazyQuery<
  TPermissionAllWithoutPaginationQueries,
  PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.concretings.id"]), {
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONCRETING
        }
      ]
    }
  });
  // endregion Get Permissions according to the user and the client
  // region Get Travels
  const [
    getTravels,
    { loading: loadingTravels, error: errorTravels, data: dataTravels }
  ] = useLazyQuery<
    TTravelAllWithPaginationQueries,
    TravelQueriesGetAllWithPaginationArgs
  >(
    travelAllWithPagination([
      "id",
      "volume",
      "fleetExternalId",
      "status",
      "loadLabel",
      "documentNumber",
      "scheduledTime",
      "invoiceIssueTime",
      "plantDepartureTime",
      "dischargeStartTime",
      "dischargeEndTime",
      "finishTime",
      "constructionStartTime",
      "constructionEndTime",
      "concreteMixer.description",
      "concreteMixer.code",
      "concreteMixer.licensePlate",
      "driver.name"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );
  // endregion Get Travels

  /**
   * Prepare the filters to be used in the query to get the travels
   */
  const filters = useMemo(() => {

    const defaultFilter = [
      {
        property: "concreting.id",
        operator: EWhereClausesOperators.IN,
        value: whereConcretingIds
      },
      {
        property: "status",
        operator: EWhereClausesOperators.IN,
        value: `${ETravelStatus.CANCELED}|${ETravelStatus.DELIVERED}|${ETravelStatus.DISCHARGING}|
                ${ETravelStatus.INVOICE_ISSUED}|${ETravelStatus.IN_CONSTRUCTION}|${ETravelStatus.IN_TRANSIT}|
                ${ETravelStatus.PREPARING}`
      }
    ];

    const today = new Date();
    const startDay = startOfDay(today).toISOString();
    const endDay = endOfDay(today).toISOString();

    return [
      ...defaultFilter,
      {
        property: "concreting.scheduledDate",
        operator: EWhereClausesOperators.GREATER_THAN_OR_EQUAL,
        value: startDay
      },
      {
        property: "concreting.scheduledDate",
        operator: EWhereClausesOperators.LESS_THAN_OR_EQUAL,
        value: endDay
      }
    ];
  }, [whereConcretingIds]);

  /**
   * Load the travels
   */
  const loadTravels = useCallback(async () => {

    if (whereConcretingIds === "") return;

    const response = dataTravels?.travel.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      const widgetTravels = utils.groupAndSortTravels(response?.result ?? []);

      setTravels(widgetTravels.slice(0, 6));
    }
  }, [dataTravels?.travel.getAllWithPagination, whereConcretingIds]);

  /**
   * Get the concreting ids from the permissions
   */
  const makeWhereConcretingIds = useCallback(() => {

    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status !== ECommandResultStatus.SUCCESS) return;

    const concretingIds: string[] = [];

    response.result?.forEach((permission) => {
      permission?.contract?.concretings?.forEach((concreting) => {
        concretingIds.push(concreting?.id || "");
      });
    });

    const concretingIdsStr = concretingIds
      .filter((id) => id && id !== "")
      .join("|");

    if (!concretingIdsStr || concretingIdsStr === "") {

      setWhereConcretingIds("");
      setTravels([]);

      return;
    }

    setWhereConcretingIds(concretingIdsStr);

  }, [dataPermissions]);

  /**
   * Open or close the travel modal
   */
  const handleOpenTravelModal = useCallback(
    (open: boolean) => {
      setTravelModalIsOpen(open);
    },
    [setTravelModalIsOpen]
  );

  // Get travels when filters change
  useEffect(() => {
    if (whereConcretingIds !== "") {
      getTravels({
        variables: { where: filters }
      }).then();
    }
  }, [getTravels, whereConcretingIds, filters]);

  // Make where concreting ids when permissions change
  useEffect(() => {
    if (dataPermissions) makeWhereConcretingIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereConcretingIds]);

  // Load travels when data changes
  useEffect(() => {
    if (dataTravels) loadTravels().then();
    if (errorTravels) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorTravels.message
      });
    }
  }, [addToast, dataTravels, errorTravels, loadTravels]);

  // Clear travels when client changes
  useEffect(() => {
    setTravels([]);
  }, [selectedClient]);

  // Create an interval to get permissions every minute
  useEffect(() => {

    const interval = setInterval(() => {
      if (!travelModalIsOpen) {
        setWhereConcretingIds("");
        getPermissions().then();
      }
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [getPermissions, travelModalIsOpen]);

  // Get permissions when component is mounted
  useEffect(() => {
    getPermissions().then();
  }, [getPermissions]);

  return (
    <Styled.Container>
      <DashboardWebLink
        icon={concreting}
        title={t(GlobalMessages.dayDeliveryText)}
        link="/concretings"
      />
      <Styled.Content>
        {(loadingTravels || loadingPermissions) && <Loading loading />}
        {!loadingPermissions
          && !loadingTravels
          && travels
          && travels.map((travel) => {

            if (!travel) return undefined;

            return (
              <BaseCardItem
                key={travel.id}
                type="travel"
                travel={travel}
                shadowInset={false}
                controlDialog={handleOpenTravelModal}
              />
            );
          })}
      </Styled.Content>
    </Styled.Container>
  );
};

export default DashboardWebTravels;
